import React, { useState, useEffect } from 'react'
import { useSearchParams } from 'react-router-dom'
import { api, formatCurrency } from '../../utils/api'

// Design System
const theme = {
  colors: {
    charcoal: '#222725',
    sunburst: '#FFD44D',
    greenOnions: '#3BB273',
    imBlue: '#4E8098',
    crimson: '#E5625E',
    porcelain: '#F2EDE4',
    white: '#FFFFFF',
    grey100: '#F8F9F8',
    grey200: '#E7E5E4',
    grey300: '#D6D3D1',
    grey400: '#A8A29E',
    grey500: '#78716C',
    greenLight: '#E8F5EE',
    amberLight: '#FFF9E6',
    redLight: '#FCEEED',
    purple: '#8B5CF6',
    purpleLight: '#F5F3FF',
    blueLight: '#EDF3F6'
  },
  fonts: { sans: "'DM Sans', -apple-system, BlinkMacSystemFont, sans-serif" },
  radius: { sm: '6px', md: '10px', lg: '16px' },
  shadows: { sm: '0 1px 2px rgba(0,0,0,0.05)', md: '0 4px 12px rgba(0,0,0,0.08)' }
}

// Constants
const CONDITION_OPTIONS = ['Broken', 'Fair', 'Good', 'Excellent', 'Like New']
const PHYSICAL_SCORE_OPTIONS = ['Poor', 'Fair', 'Good', 'Excellent', 'Like New']
const TESTING_LEVELS_ELECTRICAL = ['Fully Tested', 'Partially Tested', 'Power On Only', 'Untested']
const TESTING_LEVELS_NON_ELECTRICAL = ['Fully Tested', 'Partially Tested', 'Untested']
const CHECKLIST_RESULTS = ['OK', 'FAIL', 'N/A']

const PHYSICAL_FIELDS = [
  { key: 'scratching', label: 'Scratching & Scraping' },
  { key: 'dings', label: 'Dings and Dents' },
  { key: 'colouration', label: 'Colouration' },
  { key: 'cleanliness', label: 'Cleanliness' },
  { key: 'integrity', label: 'Physical Integrity' }
]

// Components
const Card = ({ children, style = {}, padding = true }) => (
  <div style={{
    background: theme.colors.white,
    borderRadius: theme.radius.lg,
    boxShadow: theme.shadows.sm,
    border: `1px solid ${theme.colors.grey200}`,
    padding: padding ? '24px' : 0,
    ...style
  }}>
    {children}
  </div>
)

const Badge = ({ children, color = 'grey' }) => {
  const colors = {
    grey: { bg: theme.colors.grey200, text: theme.colors.grey500 },
    green: { bg: theme.colors.greenLight, text: theme.colors.greenOnions },
    amber: { bg: theme.colors.amberLight, text: '#B8860B' },
    red: { bg: theme.colors.redLight, text: theme.colors.crimson },
    purple: { bg: theme.colors.purpleLight, text: theme.colors.purple },
    blue: { bg: theme.colors.blueLight, text: theme.colors.imBlue }
  }
  const c = colors[color] || colors.grey
  return (
    <span style={{
      display: 'inline-flex',
      padding: '4px 10px',
      borderRadius: '20px',
      fontSize: '12px',
      fontWeight: 600,
      background: c.bg,
      color: c.text
    }}>
      {children}
    </span>
  )
}

const Button = ({ children, variant = 'primary', size = 'md', fullWidth = false, disabled = false, onClick, style = {} }) => {
  const variants = {
    primary: { background: theme.colors.sunburst, color: theme.colors.charcoal },
    secondary: { background: theme.colors.white, color: theme.colors.charcoal, border: `1px solid ${theme.colors.grey300}` },
    success: { background: theme.colors.greenOnions, color: theme.colors.white },
    danger: { background: theme.colors.crimson, color: theme.colors.white },
    warning: { background: theme.colors.sunburst, color: theme.colors.charcoal },
    ghost: { background: 'transparent', color: theme.colors.grey500 }
  }
  const sizes = {
    sm: { padding: '8px 14px', fontSize: '13px' },
    md: { padding: '12px 20px', fontSize: '14px' },
    lg: { padding: '16px 28px', fontSize: '16px' }
  }
  const v = variants[variant]
  const s = sizes[size]
  return (
    <button onClick={onClick} disabled={disabled} style={{
      ...v, ...s, ...style,
      border: v.border || 'none',
      borderRadius: theme.radius.md,
      fontWeight: 600,
      cursor: disabled ? 'not-allowed' : 'pointer',
      opacity: disabled ? 0.5 : 1,
      width: fullWidth ? '100%' : 'auto',
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '8px'
    }}>
      {children}
    </button>
  )
}

const Spinner = ({ size = 20, light = false }) => (
  <div style={{
    width: size, height: size,
    border: `2px solid ${light ? 'rgba(255,255,255,0.3)' : theme.colors.grey200}`,
    borderTopColor: light ? 'white' : theme.colors.charcoal,
    borderRadius: '50%',
    animation: 'spin 0.8s linear infinite'
  }}>
    <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
  </div>
)

const Alert = ({ type = 'info', children }) => {
  const types = {
    info: { bg: theme.colors.blueLight, border: theme.colors.imBlue, icon: 'ℹ️' },
    warning: { bg: theme.colors.amberLight, border: theme.colors.sunburst, icon: '⚠️' },
    error: { bg: theme.colors.redLight, border: theme.colors.crimson, icon: '✕' },
    success: { bg: theme.colors.greenLight, border: theme.colors.greenOnions, icon: '✓' }
  }
  const t = types[type]
  return (
    <div style={{
      background: t.bg,
      border: `1px solid ${t.border}`,
      borderRadius: theme.radius.md,
      padding: '12px 16px',
      fontSize: '14px',
      display: 'flex',
      gap: '10px',
      marginBottom: '20px'
    }}>
      <span>{t.icon}</span>
      <div style={{ flex: 1 }}>{children}</div>
    </div>
  )
}

// Option Button Group
const OptionGroup = ({ options, value, onChange, columns = 5 }) => (
  <div style={{ display: 'grid', gridTemplateColumns: `repeat(${columns}, 1fr)`, gap: '8px' }}>
    {options.map(opt => (
      <button
        key={opt}
        onClick={() => onChange(opt)}
        style={{
          padding: '10px 8px',
          border: `2px solid ${value === opt ? theme.colors.greenOnions : theme.colors.grey200}`,
          borderRadius: theme.radius.md,
          background: value === opt ? theme.colors.greenLight : theme.colors.white,
          fontWeight: value === opt ? 600 : 400,
          fontSize: '13px',
          cursor: 'pointer',
          transition: 'all 0.15s ease'
        }}
      >
        {opt}
      </button>
    ))}
  </div>
)

// Item Tabs Component for multi-item deals
function ItemTabs({ items, currentIndex, completedIndices, onSelectItem, hasUnsavedChanges }) {
  const containerRef = React.useRef(null)

  const getItemStatus = (index) => {
    if (completedIndices.includes(index)) return 'completed'
    if (index === currentIndex) return 'current'
    return 'pending'
  }

  const handleTabClick = (index) => {
    if (index === currentIndex) return

    if (hasUnsavedChanges) {
      if (confirm('You have unsaved changes on this item. Switch anyway? Your progress will be lost.')) {
        onSelectItem(index)
      }
    } else {
      onSelectItem(index)
    }
  }

  return (
    <div style={{ marginBottom: '20px' }}>
      <div
        ref={containerRef}
        style={{
          display: 'flex',
          gap: '8px',
          overflowX: items.length > 5 ? 'auto' : 'visible',
          paddingBottom: items.length > 5 ? '8px' : 0,
          scrollbarWidth: 'thin'
        }}
      >
        {items.map((item, index) => {
          const status = getItemStatus(index)
          const shortName = (item.name || item.productName || `Item ${index + 1}`).substring(0, 25)
          const displayName = shortName.length < (item.name || item.productName || '').length
            ? shortName + '...'
            : shortName

          return (
            <button
              key={index}
              onClick={() => handleTabClick(index)}
              style={{
                flex: items.length <= 5 ? 1 : '0 0 auto',
                minWidth: items.length > 5 ? '140px' : 'auto',
                padding: '12px 16px',
                border: status === 'current'
                  ? `2px solid ${theme.colors.sunburst}`
                  : `1px solid ${theme.colors.grey200}`,
                borderRadius: theme.radius.md,
                background: status === 'completed'
                  ? theme.colors.greenLight
                  : status === 'current'
                    ? theme.colors.white
                    : theme.colors.grey100,
                cursor: 'pointer',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: '4px',
                transition: 'all 0.15s ease',
                position: 'relative'
              }}
            >
              {/* Status indicator */}
              <div style={{
                position: 'absolute',
                top: '6px',
                right: '6px',
                width: '16px',
                height: '16px',
                borderRadius: '50%',
                background: status === 'completed'
                  ? theme.colors.greenOnions
                  : status === 'current'
                    ? theme.colors.sunburst
                    : theme.colors.grey300,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '10px',
                color: status === 'completed' ? 'white' : theme.colors.charcoal,
                fontWeight: 600
              }}>
                {status === 'completed' ? '✓' : index + 1}
              </div>

              {/* Item number */}
              <span style={{
                fontSize: '11px',
                color: theme.colors.grey500,
                fontWeight: 500
              }}>
                Item {index + 1}
              </span>

              {/* Item name */}
              <span style={{
                fontSize: '12px',
                fontWeight: status === 'current' ? 600 : 400,
                color: theme.colors.charcoal,
                textAlign: 'center',
                lineHeight: 1.2,
                maxWidth: '120px',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap'
              }}>
                {displayName}
              </span>
            </button>
          )
        })}
      </div>

      {/* Progress indicator */}
      <div style={{
        marginTop: '12px',
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        fontSize: '13px',
        color: theme.colors.grey500
      }}>
        <div style={{
          flex: 1,
          height: '4px',
          background: theme.colors.grey200,
          borderRadius: '2px',
          overflow: 'hidden'
        }}>
          <div style={{
            height: '100%',
            width: `${(completedIndices.length / items.length) * 100}%`,
            background: theme.colors.greenOnions,
            borderRadius: '2px',
            transition: 'width 0.3s ease'
          }} />
        </div>
        <span>{completedIndices.length} of {items.length} completed</span>
      </div>
    </div>
  )
}

// Product Type Selector
function ProductTypeSelector({ productTypes, onSelect, onCancel, loading }) {
  const [searchTerm, setSearchTerm] = useState('')
  const [expandedBuckets, setExpandedBuckets] = useState({})

  const toggleBucket = (bucket) => {
    setExpandedBuckets(prev => ({ ...prev, [bucket]: !prev[bucket] }))
  }

  const filteredGroups = productTypes?.groups?.map(group => {
    if (!searchTerm) return group
    const filtered = group.productTypes.filter(type =>
      type.toLowerCase().includes(searchTerm.toLowerCase())
    )
    return filtered.length > 0 ? { ...group, productTypes: filtered } : null
  }).filter(Boolean) || []

  useEffect(() => {
    if (searchTerm) {
      const expanded = {}
      filteredGroups.forEach(g => { expanded[g.bucket] = true })
      setExpandedBuckets(expanded)
    }
  }, [searchTerm])

  return (
    <Card style={{ marginBottom: '20px', border: `2px solid ${theme.colors.crimson}` }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
        <h4 style={{ fontWeight: 600, color: theme.colors.crimson, margin: 0 }}>Select Product Type</h4>
        <Button variant="ghost" size="sm" onClick={onCancel}>Cancel</Button>
      </div>

      <input
        type="text"
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        placeholder="Search product types..."
        style={{
          width: '100%', padding: '12px 16px',
          border: `1px solid ${theme.colors.grey300}`,
          borderRadius: theme.radius.md, fontSize: '14px',
          marginBottom: '16px', outline: 'none'
        }}
      />

      {loading ? (
        <div style={{ textAlign: 'center', padding: '20px' }}><Spinner /></div>
      ) : (
        <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
          {filteredGroups.map(group => (
            <div key={group.bucket} style={{ marginBottom: '8px' }}>
              <button
                onClick={() => toggleBucket(group.bucket)}
                style={{
                  width: '100%', padding: '12px 16px',
                  background: theme.colors.grey100, border: 'none',
                  borderRadius: theme.radius.md, cursor: 'pointer',
                  display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                  fontWeight: 600, fontSize: '14px'
                }}
              >
                <span>{group.bucket}</span>
                <span style={{ color: theme.colors.grey500 }}>
                  {expandedBuckets[group.bucket] ? '▼' : '▶'} ({group.productTypes.length})
                </span>
              </button>

              {expandedBuckets[group.bucket] && (
                <div style={{ padding: '8px 0 8px 16px', display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                  {group.productTypes.map(type => (
                    <button
                      key={type}
                      onClick={() => onSelect(type, group.bucket)}
                      style={{
                        padding: '8px 14px',
                        border: `1px solid ${theme.colors.grey300}`,
                        borderRadius: theme.radius.md,
                        background: theme.colors.white,
                        cursor: 'pointer', fontSize: '13px'
                      }}
                    >
                      {type}
                    </button>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </Card>
  )
}

function Testing() {
  const [searchParams, setSearchParams] = useSearchParams()
  const [searchQuery, setSearchQuery] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [searchResults, setSearchResults] = useState([])
  const [deal, setDeal] = useState(null)
  const [currentItemIndex, setCurrentItemIndex] = useState(0)
  const [step, setStep] = useState('search') // search, results, testing, success
  const [productTypes, setProductTypes] = useState(null)
  const [productTypesLoading, setProductTypesLoading] = useState(false)
  const [showProductTypeSelector, setShowProductTypeSelector] = useState(false)
  const [checklist, setChecklist] = useState(null)
  const [includedItemsOptions, setIncludedItemsOptions] = useState([])
  const [saving, setSaving] = useState(false)

  // Store all item results locally until final submission
  const [allItemResults, setAllItemResults] = useState([])
  const [submitResult, setSubmitResult] = useState(null) // API response after final submit
  const [showDraftConfirm, setShowDraftConfirm] = useState(false)
  const [creatingDrafts, setCreatingDrafts] = useState(false)
  const [draftsCreated, setDraftsCreated] = useState([])
  const [draftProgress, setDraftProgress] = useState({ current: 0, total: 0 })
  const [draftError, setDraftError] = useState(null)

  // Per-item test results (current item being tested)
  const [testResult, setTestResult] = useState({
    overallCondition: null,
    brokenDetails: '',
    sparesAmount: '',
    physicalCondition: {
      scratching: null,
      dings: null,
      colouration: null,
      cleanliness: null,
      integrity: null
    },
    testingLevel: null,
    checklistResults: {},
    includedItems: [],
    otherAccessories: '', // Free text for "Other" accessories
    notes: '',
    needsRepair: false,
    repairIssues: ''
  })

  const currentItem = deal?.items?.[currentItemIndex]
  const isElectrical = checklist?.electrical !== false
  const testingLevelOptions = isElectrical ? TESTING_LEVELS_ELECTRICAL : TESTING_LEVELS_NON_ELECTRICAL

  // Get indices of completed items
  const completedItemIndices = allItemResults.map(r => r.itemIndex)

  // Check if current item has unsaved changes
  const hasUnsavedChanges = () => {
    // If item is already completed, no unsaved changes
    if (completedItemIndices.includes(currentItemIndex)) return false
    // Check if any field has been filled in
    if (testResult.overallCondition) return true
    if (testResult.brokenDetails) return true
    if (testResult.sparesAmount) return true
    if (testResult.testingLevel) return true
    if (testResult.notes) return true
    if (testResult.includedItems.length > 0) return true
    if (testResult.needsRepair) return true
    if (Object.values(testResult.physicalCondition).some(v => v !== null)) return true
    if (Object.keys(testResult.checklistResults).length > 0) return true
    return false
  }

  // Switch to a different item
  const switchToItem = async (index) => {
    if (index === currentItemIndex) return

    setCurrentItemIndex(index)
    setShowProductTypeSelector(false)

    // Check if this item was already completed - restore its data
    const existingResult = allItemResults.find(r => r.itemIndex === index)
    if (existingResult) {
      setTestResult({
        overallCondition: existingResult.overallCondition,
        brokenDetails: existingResult.brokenDetails || '',
        sparesAmount: existingResult.sparesAmount?.toString() || '',
        physicalCondition: existingResult.physicalCondition || {
          scratching: null, dings: null, colouration: null, cleanliness: null, integrity: null
        },
        testingLevel: existingResult.testingLevel,
        checklistResults: existingResult.checklistResults || {},
        includedItems: existingResult.includedItems || [],
        otherAccessories: existingResult.otherAccessories || '',
        notes: existingResult.notes || '',
        needsRepair: existingResult.needsRepair || false,
        repairIssues: existingResult.repairIssues || ''
      })
    } else {
      resetTestResult()
    }

    // Load checklist for new item
    const newItem = deal?.items?.[index]
    if (newItem?.productType) {
      await loadChecklist(newItem.productType)
    } else {
      setChecklist(null)
      setIncludedItemsOptions([])
    }
  }

  // Broken item detection
  const isBroken = testResult.overallCondition === 'Broken'
  const statedNotWorking = currentItem?.customerCondition === 'Not Working'
  const isUnexpectedlyBroken = isBroken && !statedNotWorking

  // Auto-load deal from URL query parameter
  useEffect(() => {
    const dealId = searchParams.get('dealId')
    if (dealId && step === 'search' && !deal) {
      setSearchQuery(dealId)
      loadDealById(dealId)
      setSearchParams({})
    }
  }, [searchParams])

  const resetTestResult = () => {
    setTestResult({
      overallCondition: null,
      brokenDetails: '',
      sparesAmount: '',
      physicalCondition: {
        scratching: null,
        dings: null,
        colouration: null,
        cleanliness: null,
        integrity: null
      },
      testingLevel: null,
      checklistResults: {},
      includedItems: [],
      otherAccessories: '',
      notes: '',
      needsRepair: false,
      repairIssues: ''
    })
  }

  const loadDealById = async (dealId) => {
    setLoading(true)
    setError(null)
    try {
      const response = await api.get(`/api/warehouse/testing/search?q=${encodeURIComponent(dealId)}`)
      const deals = response.deals || []
      if (deals.length === 0) {
        setError('Deal not found or not in "Arrived to Test" stage.')
      } else {
        selectDeal(deals[0])
      }
    } catch (err) {
      setError(err.message || 'Failed to load deal')
    } finally {
      setLoading(false)
    }
  }

  const handleSearch = async () => {
    if (!searchQuery.trim()) return
    setLoading(true)
    setError(null)
    setSearchResults([])
    try {
      const response = await api.get(`/api/warehouse/testing/search?q=${encodeURIComponent(searchQuery)}`)
      const deals = response.deals || []
      if (deals.length === 0) {
        setError('No deals found in "Arrived to Test" stage.')
      } else if (deals.length === 1) {
        selectDeal(deals[0])
      } else {
        setSearchResults(deals)
        setStep('results')
      }
    } catch (err) {
      setError(err.message || 'Search failed')
    } finally {
      setLoading(false)
    }
  }

  const selectDeal = async (selectedDeal) => {
    setDeal(selectedDeal)
    setCurrentItemIndex(0)
    resetTestResult()
    setAllItemResults([]) // Reset collected results for new deal
    setSubmitResult(null)
    setStep('testing')
    if (selectedDeal.items?.[0]?.productType) {
      await loadChecklist(selectedDeal.items[0].productType)
    }
  }

  const loadChecklist = async (productType) => {
    if (!productType) {
      setChecklist(null)
      setIncludedItemsOptions([])
      return
    }
    try {
      const response = await api.get(`/api/warehouse/testing/checklist?productType=${encodeURIComponent(productType)}`)
      setChecklist(response)
      setIncludedItemsOptions(response.includedItems || [])
    } catch (err) {
      setChecklist(null)
      setIncludedItemsOptions([])
    }
  }

  const loadProductTypes = async () => {
    if (productTypes) return
    setProductTypesLoading(true)
    try {
      const response = await api.get('/api/warehouse/testing/product-types')
      setProductTypes(response)
    } catch (err) {
      console.error('Failed to load product types:', err)
    } finally {
      setProductTypesLoading(false)
    }
  }

  const openProductTypeSelector = async () => {
    await loadProductTypes()
    setShowProductTypeSelector(true)
  }

  const handleProductTypeSelect = async (productType, bucket) => {
    setDeal(prev => ({
      ...prev,
      items: prev.items.map((item, idx) =>
        idx === currentItemIndex ? { ...item, productType, testingBucket: bucket } : item
      )
    }))

    // Persist product type to D1 using the product name for future lookups
    try {
      const productName = currentItem?.productName || currentItem?.name
      if (productName) {
        await api.post('/api/warehouse/testing/set-product-type', {
          productName,
          productType
        })
      }
    } catch (err) {
      console.error('Failed to persist product type:', err)
    }

    await loadChecklist(productType)
    setShowProductTypeSelector(false)
  }

  const updatePhysicalCondition = (field, value) => {
    setTestResult(prev => ({
      ...prev,
      physicalCondition: { ...prev.physicalCondition, [field]: value }
    }))
  }

  const updateChecklistResult = (key, value) => {
    setTestResult(prev => ({
      ...prev,
      checklistResults: { ...prev.checklistResults, [key]: value }
    }))
  }

  const toggleIncludedItem = (item) => {
    setTestResult(prev => ({
      ...prev,
      includedItems: prev.includedItems.includes(item)
        ? prev.includedItems.filter(i => i !== item)
        : [...prev.includedItems, item]
    }))
  }

  const canSubmit = () => {
    if (!testResult.overallCondition) return false
    if (!currentItem?.productType) return false

    if (isUnexpectedlyBroken) {
      if (!testResult.sparesAmount || !testResult.brokenDetails) return false
    } else if (!isBroken) {
      // Physical condition required for non-broken
      const physicalComplete = Object.values(testResult.physicalCondition).every(v => v !== null)
      if (!physicalComplete) return false
      if (!testResult.testingLevel) return false
    }

    return true
  }

  const saveResults = async () => {
    setSaving(true)
    setError(null)
    try {
      // Format the result for this item
      const itemResult = {
        itemId: currentItem.id,
        itemName: currentItem.name || currentItem.productName,
        itemIndex: currentItemIndex,
        overallCondition: testResult.overallCondition,
        brokenDetails: testResult.brokenDetails,
        sparesAmount: testResult.sparesAmount ? parseFloat(testResult.sparesAmount) : null,
        physicalCondition: testResult.physicalCondition,
        testingLevel: testResult.testingLevel,
        checklistResults: testResult.checklistResults,
        includedItems: testResult.includedItems,
        otherAccessories: testResult.otherAccessories,
        notes: testResult.notes,
        needsRepair: testResult.needsRepair,
        repairIssues: testResult.repairIssues
      }

      // Update or add result (support re-testing items)
      const existingIndex = allItemResults.findIndex(r => r.itemIndex === currentItemIndex)
      let updatedResults
      if (existingIndex >= 0) {
        // Update existing result
        updatedResults = [...allItemResults]
        updatedResults[existingIndex] = itemResult
      } else {
        // Add new result
        updatedResults = [...allItemResults, itemResult]
      }
      setAllItemResults(updatedResults)

      // Check if ALL items are now complete
      const allItemsComplete = deal.items.every((_, idx) =>
        updatedResults.some(r => r.itemIndex === idx)
      )

      if (allItemsComplete) {
        // Submit all results together
        const response = await api.post('/api/warehouse/testing/save-results', {
          dealId: deal.id,
          itemResults: updatedResults,
          items: deal.items,
          isListingService: deal.isListingService || false
        })
        setSubmitResult(response)
        setStep('success')
      } else {
        // Find next incomplete item
        const completedIndices = updatedResults.map(r => r.itemIndex)
        let nextIndex = -1

        // First try to find the next incomplete item after current
        for (let i = currentItemIndex + 1; i < deal.items.length; i++) {
          if (!completedIndices.includes(i)) {
            nextIndex = i
            break
          }
        }

        // If not found, look from the beginning
        if (nextIndex === -1) {
          for (let i = 0; i < currentItemIndex; i++) {
            if (!completedIndices.includes(i)) {
              nextIndex = i
              break
            }
          }
        }

        // Move to next incomplete item
        if (nextIndex >= 0) {
          setCurrentItemIndex(nextIndex)
          resetTestResult()
          setShowProductTypeSelector(false)
          const nextItem = deal.items[nextIndex]
          if (nextItem?.productType) {
            await loadChecklist(nextItem.productType)
          } else {
            setChecklist(null)
            setIncludedItemsOptions([])
          }
        }
      }
    } catch (err) {
      setError(err.message || 'Failed to save results')
    } finally {
      setSaving(false)
    }
  }

  const reset = () => {
    setSearchQuery('')
    setSearchResults([])
    setDeal(null)
    setCurrentItemIndex(0)
    setStep('search')
    setChecklist(null)
    setIncludedItemsOptions([])
    resetTestResult()
    setError(null)
    setShowProductTypeSelector(false)
    setAllItemResults([])
    setSubmitResult(null)
    setShowDraftConfirm(false)
    setCreatingDrafts(false)
    setDraftsCreated([])
    setDraftProgress({ current: 0, total: 0 })
    setDraftError(null)
  }

  // Format accessory ID to display name (e.g., "original_box" -> "Original Box")
  const formatAccessoryName = (id) => {
    if (!id) return id
    return id
      .replace(/_/g, ' ')
      .replace(/\b\w/g, c => c.toUpperCase())
  }

  const createShopifyDrafts = async (passedItemResults) => {
    setCreatingDrafts(true)
    setDraftError(null)
    setDraftsCreated([])
    setDraftProgress({ current: 0, total: passedItemResults.length })
    setShowDraftConfirm(false)

    const created = []

    for (let i = 0; i < passedItemResults.length; i++) {
      const itemResult = passedItemResults[i]
      const item = deal.items[itemResult.itemIndex]

      // Format accessory names properly (convert IDs to display names)
      // Filter out 'other' from the list and add the actual other text if provided
      const includedItems = (itemResult.includedItems || []).filter(id => id !== 'other')
      const formattedAccessories = includedItems.map(formatAccessoryName)

      // Add "other" accessories text if provided
      if (itemResult.includedItems?.includes('other') && itemResult.otherAccessories?.trim()) {
        formattedAccessories.push(itemResult.otherAccessories.trim())
      }

      try {
        // Build the payload matching warehouse-system's expected format
        const response = await api.post('/api/warehouse/testing/create-draft', {
          // From deal item (parsed_items data)
          productName: item.name || item.productName,
          productType: item.productType,
          quantity: item.quantity || 1,
          sellPrice: item.sellPrice,
          buyPrice: item.buyPrice,

          // From testing results
          overallCondition: itemResult.overallCondition,
          physicalCondition: itemResult.physicalCondition,
          testingLevel: itemResult.testingLevel,
          checklistResults: itemResult.checklistResults,
          notes: itemResult.notes,
          brokenDetails: itemResult.brokenDetails,

          // Accessories (included items) - formatted properly
          accessories: formattedAccessories,

          // Source info
          dealId: deal.id,
          itemIndex: itemResult.itemIndex,

          // Not listing service (manual draft creation)
          isListingService: false
        })

        if (response.success) {
          created.push({
            itemName: item.name || item.productName,
            draftId: response.shopifyProductId,
            sku: response.sku,
            success: true
          })
        } else {
          created.push({
            itemName: item.name || item.productName,
            success: false,
            error: response.error || 'Failed to create draft'
          })
        }
      } catch (err) {
        created.push({
          itemName: item.name || item.productName,
          success: false,
          error: err.message || 'Failed to create draft'
        })
      }

      setDraftProgress({ current: i + 1, total: passedItemResults.length })
      setDraftsCreated([...created])
    }

    setCreatingDrafts(false)
  }

  // Search Screen
  if (step === 'search') {
    return (
      <div style={{ maxWidth: '480px', margin: '60px auto', padding: '0 24px' }}>
        <Card style={{ padding: '40px' }}>
          <div style={{ textAlign: 'center', marginBottom: '32px' }}>
            <div style={{ fontSize: '56px', marginBottom: '16px' }}>🔧</div>
            <h2 style={{ fontSize: '24px', fontWeight: 700, marginBottom: '8px' }}>Product Testing</h2>
            <p style={{ color: theme.colors.grey500 }}>Enter a Deal ID to begin testing</p>
          </div>

          <div style={{ marginBottom: '24px' }}>
            <input
              type="text"
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              onKeyPress={e => e.key === 'Enter' && handleSearch()}
              placeholder="Enter Deal ID"
              style={{
                width: '100%', padding: '14px 16px',
                border: `1px solid ${theme.colors.grey300}`,
                borderRadius: theme.radius.md, fontSize: '15px',
                fontFamily: theme.fonts.sans,
                textAlign: 'center', outline: 'none'
              }}
            />
          </div>

          {error && <Alert type="error">{error}</Alert>}

          <Button onClick={handleSearch} disabled={loading || !searchQuery.trim()} fullWidth size="lg">
            {loading ? <><Spinner size={18} /> Searching...</> : 'Search'}
          </Button>
        </Card>
      </div>
    )
  }

  // Results Screen
  if (step === 'results') {
    return (
      <div style={{ maxWidth: '600px', margin: '40px auto', padding: '0 24px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
          <h2 style={{ fontSize: '20px', fontWeight: 700 }}>
            {searchResults.length} Deal{searchResults.length !== 1 ? 's' : ''} Found
          </h2>
          <Button variant="secondary" size="sm" onClick={reset}>New Search</Button>
        </div>

        {searchResults.map(d => (
          <Card key={d.id} onClick={() => selectDeal(d)} style={{ marginBottom: '12px', cursor: 'pointer' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <div>
                <div style={{ fontFamily: theme.fonts.sans, fontSize: '13px', color: theme.colors.grey500, marginBottom: '4px' }}>
                  {d.id}
                </div>
                <div style={{ fontWeight: 600, fontSize: '16px', marginBottom: '8px' }}>
                  {d.customerName || d.dealName}
                </div>
                <div style={{ fontSize: '14px', color: theme.colors.grey500 }}>
                  {d.items?.length || 0} item{(d.items?.length || 0) !== 1 ? 's' : ''}
                </div>
              </div>
              {d.postcode && <Badge>{d.postcode}</Badge>}
            </div>
          </Card>
        ))}
      </div>
    )
  }

  // Testing Screen
  if (step === 'testing' && deal && currentItem) {
    return (
      <div style={{ maxWidth: '700px', margin: '40px auto', padding: '0 24px' }}>
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
          <div>
            <div style={{ fontFamily: theme.fonts.sans, fontSize: '13px', color: theme.colors.grey500, marginBottom: '4px' }}>
              {deal.id}
            </div>
            <h2 style={{ fontSize: '20px', fontWeight: 700, margin: 0 }}>
              Product Testing
            </h2>
          </div>
          <Button variant="secondary" size="sm" onClick={reset}>New Search</Button>
        </div>

        {/* Item Tabs - for multi-item deals */}
        {deal.items.length > 1 && (
          <ItemTabs
            items={deal.items}
            currentIndex={currentItemIndex}
            completedIndices={completedItemIndices}
            onSelectItem={switchToItem}
            hasUnsavedChanges={hasUnsavedChanges()}
          />
        )}

        {/* Item Info */}
        <Card style={{ marginBottom: '20px' }}>
          <div style={{ fontWeight: 600, fontSize: '18px', marginBottom: '8px' }}>
            {currentItem.name || currentItem.productName || `Item ${currentItemIndex + 1}`}
          </div>
          <div style={{ display: 'flex', gap: '16px', color: theme.colors.grey500, fontSize: '14px', flexWrap: 'wrap' }}>
            {currentItem.buyPrice && <span>Buy: {formatCurrency(currentItem.buyPrice)}</span>}
            {currentItem.sellPrice && <span>Sell: {formatCurrency(currentItem.sellPrice)}</span>}
            {currentItem.productType && <Badge color="blue">{currentItem.productType}</Badge>}
          </div>
        </Card>

        {/* Product Type - Required */}
        {showProductTypeSelector ? (
          <ProductTypeSelector
            productTypes={productTypes}
            onSelect={handleProductTypeSelect}
            onCancel={() => setShowProductTypeSelector(false)}
            loading={productTypesLoading}
          />
        ) : !currentItem.productType ? (
          <Alert type="error">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
              <span>Product type not found in database. Select a type to continue testing.</span>
              <Button variant="secondary" size="sm" onClick={openProductTypeSelector}>Select Type</Button>
            </div>
          </Alert>
        ) : (
          <Card style={{ marginBottom: '20px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <div style={{ fontSize: '13px', color: theme.colors.grey500 }}>Product Type</div>
                <div style={{ fontWeight: 600, marginTop: '4px' }}>{currentItem.productType}</div>
                {checklist?.bucket && (
                  <div style={{ fontSize: '12px', color: theme.colors.grey400, marginTop: '2px' }}>
                    Testing Bucket: {checklist.bucket}
                  </div>
                )}
              </div>
              <Button variant="ghost" size="sm" onClick={openProductTypeSelector}>Change</Button>
            </div>
          </Card>
        )}

        {/* Customer Stated Condition */}
        {currentItem.customerCondition && (
          <Card style={{ marginBottom: '20px' }}>
            <div style={{ fontSize: '14px' }}>
              Customer stated condition: <strong>{currentItem.customerCondition}</strong>
            </div>
          </Card>
        )}

        {/* Only show rest of form if product type is set */}
        {currentItem.productType && (
          <>
            {/* 1. Overall Condition */}
            <Card style={{ marginBottom: '20px' }}>
              <div style={{ fontWeight: 600, marginBottom: '12px' }}>Overall Condition *</div>
              <OptionGroup
                options={CONDITION_OPTIONS}
                value={testResult.overallCondition}
                onChange={(val) => setTestResult(prev => ({ ...prev, overallCondition: val }))}
              />
            </Card>

            {/* 2. Broken Item - Expected */}
            {isBroken && statedNotWorking && (
              <Alert type="info">
                Item was stated as "Not Working" by customer. Continue with broken item documentation.
              </Alert>
            )}

            {/* 2. Broken Item - Unexpected */}
            {isUnexpectedlyBroken && (
              <Card style={{ marginBottom: '20px', border: `2px solid ${theme.colors.crimson}` }}>
                <div style={{ fontWeight: 600, marginBottom: '16px', color: theme.colors.crimson }}>
                  Unexpectedly Broken - Customer Expected Working
                </div>

                <div style={{ marginBottom: '16px' }}>
                  <div style={{ fontSize: '13px', color: theme.colors.grey500, marginBottom: '4px' }}>
                    Original Buy Price: <strong>{formatCurrency(currentItem.buyPrice || 0)}</strong>
                  </div>
                </div>

                <div style={{ marginBottom: '16px' }}>
                  <label style={{ fontSize: '13px', color: theme.colors.grey500, display: 'block', marginBottom: '8px' }}>
                    Spares/Repair Offer Amount *
                  </label>
                  <input
                    type="number"
                    value={testResult.sparesAmount}
                    onChange={(e) => setTestResult(prev => ({ ...prev, sparesAmount: e.target.value }))}
                    placeholder="Enter amount to offer"
                    style={{
                      width: '100%', padding: '12px',
                      border: `1px solid ${theme.colors.grey300}`,
                      borderRadius: theme.radius.md, fontSize: '14px', outline: 'none'
                    }}
                  />
                </div>

                <div>
                  <label style={{ fontSize: '13px', color: theme.colors.grey500, display: 'block', marginBottom: '8px' }}>
                    Describe the issue *
                  </label>
                  <textarea
                    value={testResult.brokenDetails}
                    onChange={(e) => setTestResult(prev => ({ ...prev, brokenDetails: e.target.value }))}
                    placeholder="Describe what's wrong with the item..."
                    rows={3}
                    style={{
                      width: '100%', padding: '12px',
                      border: `1px solid ${theme.colors.grey300}`,
                      borderRadius: theme.radius.md, fontSize: '14px',
                      resize: 'vertical', outline: 'none'
                    }}
                  />
                </div>
              </Card>
            )}

            {/* 3. Physical Condition - Only if NOT unexpectedly broken */}
            {testResult.overallCondition && !isUnexpectedlyBroken && (
              <Card style={{ marginBottom: '20px' }}>
                <div style={{ fontWeight: 600, marginBottom: '16px' }}>Physical Condition Assessment *</div>
                {PHYSICAL_FIELDS.map(field => (
                  <div key={field.key} style={{ marginBottom: '16px' }}>
                    <div style={{ fontSize: '13px', color: theme.colors.grey500, marginBottom: '8px' }}>{field.label}</div>
                    <OptionGroup
                      options={PHYSICAL_SCORE_OPTIONS}
                      value={testResult.physicalCondition[field.key]}
                      onChange={(val) => updatePhysicalCondition(field.key, val)}
                    />
                  </div>
                ))}
              </Card>
            )}

            {/* 4. Testing Level - Only if NOT unexpectedly broken */}
            {testResult.overallCondition && !isUnexpectedlyBroken && (
              <Card style={{ marginBottom: '20px' }}>
                <div style={{ fontWeight: 600, marginBottom: '12px' }}>
                  Testing Level *
                  <span style={{ fontWeight: 400, color: theme.colors.grey500, fontSize: '13px', marginLeft: '8px' }}>
                    ({isElectrical ? 'Electrical' : 'Non-Electrical'} Item)
                  </span>
                </div>
                <OptionGroup
                  options={testingLevelOptions}
                  value={testResult.testingLevel}
                  onChange={(val) => setTestResult(prev => ({ ...prev, testingLevel: val }))}
                  columns={testingLevelOptions.length}
                />
              </Card>
            )}

            {/* 5. Checklist - Only if testing level is not Untested */}
            {testResult.testingLevel && testResult.testingLevel !== 'Untested' && checklist?.questions?.length > 0 && (
              <Card style={{ marginBottom: '20px' }} padding={false}>
                <div style={{ padding: '16px 20px', borderBottom: `1px solid ${theme.colors.grey200}`, fontWeight: 600 }}>
                  Test Checklist - {checklist.bucket}
                </div>
                <div style={{ padding: '12px' }}>
                  {checklist.questions.map((q) => {
                    const questionId = q.id || q.key
                    const questionLabel = q.label || (typeof q === 'string' ? q : questionId)
                    const result = testResult.checklistResults[questionId]
                    return (
                      <div
                        key={questionId}
                        style={{
                          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                          padding: '12px 14px', marginBottom: '8px',
                          background: result === 'OK' ? theme.colors.greenLight :
                            result === 'FAIL' ? theme.colors.redLight :
                            result === 'N/A' ? theme.colors.grey100 : theme.colors.white,
                          border: `1px solid ${theme.colors.grey200}`,
                          borderRadius: theme.radius.md
                        }}
                      >
                        <span style={{ fontWeight: 500, flex: 1 }}>{questionLabel}</span>
                        <div style={{ display: 'flex', gap: '6px' }}>
                          {CHECKLIST_RESULTS.map(r => (
                            <button
                              key={r}
                              onClick={() => updateChecklistResult(questionId, r)}
                              style={{
                                padding: '6px 12px', borderRadius: theme.radius.sm,
                                background: result === r
                                  ? (r === 'OK' ? theme.colors.greenOnions : r === 'FAIL' ? theme.colors.crimson : theme.colors.grey400)
                                  : theme.colors.white,
                                color: result === r ? theme.colors.white : theme.colors.charcoal,
                                fontWeight: 600, fontSize: '12px', cursor: 'pointer',
                                border: `1px solid ${theme.colors.grey200}`
                              }}
                            >
                              {r}
                            </button>
                          ))}
                        </div>
                      </div>
                    )
                  })}
                </div>
              </Card>
            )}

            {/* 6. Included Items - Only show after condition selected and not broken */}
            {includedItemsOptions.length > 0 && testResult.overallCondition && !isUnexpectedlyBroken && !isBroken && (
              <Card style={{ marginBottom: '20px' }}>
                <div style={{ fontWeight: 600, marginBottom: '12px' }}>Included Items / Accessories</div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: testResult.includedItems.includes('other') ? '12px' : 0 }}>
                  {includedItemsOptions.map(item => {
                    const itemId = item.id || item
                    const itemLabel = item.label || (typeof item === 'string' ? item : itemId)
                    const selected = testResult.includedItems.includes(itemId)
                    return (
                      <button
                        key={itemId}
                        onClick={() => toggleIncludedItem(itemId)}
                        style={{
                          padding: '8px 14px',
                          border: `2px solid ${selected ? theme.colors.greenOnions : theme.colors.grey200}`,
                          borderRadius: theme.radius.md,
                          background: selected ? theme.colors.greenLight : theme.colors.white,
                          fontWeight: selected ? 600 : 400,
                          fontSize: '13px', cursor: 'pointer'
                        }}
                      >
                        {selected && '✓ '}{itemLabel}
                      </button>
                    )
                  })}
                </div>
                {/* Other accessories text input */}
                {testResult.includedItems.includes('other') && (
                  <input
                    type="text"
                    value={testResult.otherAccessories}
                    onChange={(e) => setTestResult(prev => ({ ...prev, otherAccessories: e.target.value }))}
                    placeholder="Describe other accessories (e.g., spare strings, custom pickguard, adapter cable)"
                    style={{
                      width: '100%',
                      padding: '12px',
                      border: `1px solid ${theme.colors.grey300}`,
                      borderRadius: theme.radius.md,
                      fontSize: '14px',
                      outline: 'none'
                    }}
                  />
                )}
              </Card>
            )}

            {/* 7. Needs Repair */}
            {!isUnexpectedlyBroken && testResult.overallCondition && testResult.overallCondition !== 'Broken' && (
              <Card style={{ marginBottom: '20px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: testResult.needsRepair ? '16px' : 0 }}>
                  <input
                    type="checkbox"
                    checked={testResult.needsRepair}
                    onChange={(e) => setTestResult(prev => ({ ...prev, needsRepair: e.target.checked }))}
                    style={{ width: '20px', height: '20px', cursor: 'pointer' }}
                  />
                  <label style={{ fontWeight: 500, cursor: 'pointer' }} onClick={() => setTestResult(prev => ({ ...prev, needsRepair: !prev.needsRepair }))}>
                    Mark for Repair List
                  </label>
                </div>
                {testResult.needsRepair && (
                  <textarea
                    value={testResult.repairIssues}
                    onChange={(e) => setTestResult(prev => ({ ...prev, repairIssues: e.target.value }))}
                    placeholder="Describe repair issues..."
                    rows={3}
                    style={{
                      width: '100%', padding: '12px',
                      border: `1px solid ${theme.colors.grey300}`,
                      borderRadius: theme.radius.md, fontSize: '14px',
                      resize: 'vertical', outline: 'none'
                    }}
                  />
                )}
              </Card>
            )}

            {/* 8. Notes */}
            <Card style={{ marginBottom: '20px' }}>
              <div style={{ fontSize: '13px', color: theme.colors.grey500, marginBottom: '8px' }}>Additional Notes</div>
              <textarea
                value={testResult.notes}
                onChange={e => setTestResult(prev => ({ ...prev, notes: e.target.value }))}
                placeholder="Enter any additional notes..."
                rows={3}
                style={{
                  width: '100%', padding: '12px',
                  border: `1px solid ${theme.colors.grey300}`,
                  borderRadius: theme.radius.md, fontSize: '14px',
                  resize: 'vertical', outline: 'none'
                }}
              />
            </Card>

            {error && <Alert type="error">{error}</Alert>}

            {/* Submit Button */}
            <Button
              variant={isBroken ? 'danger' : 'success'}
              disabled={saving || !canSubmit()}
              onClick={saveResults}
              fullWidth
              size="lg"
            >
              {saving ? <><Spinner size={18} light /> Saving...</> : (
                isBroken ? 'Save & Continue (Broken)' :
                testResult.needsRepair ? 'Save & Continue (Needs Repair)' :
                'Save & Continue'
              )}
            </Button>

            {!canSubmit() && (
              <div style={{ marginTop: '12px', fontSize: '13px', color: theme.colors.grey500, textAlign: 'center' }}>
                {!testResult.overallCondition && 'Select overall condition • '}
                {!currentItem.productType && 'Set product type • '}
                {isUnexpectedlyBroken && !testResult.sparesAmount && 'Enter spares amount • '}
                {isUnexpectedlyBroken && !testResult.brokenDetails && 'Describe the issue • '}
                {!isBroken && !isUnexpectedlyBroken && !Object.values(testResult.physicalCondition).every(v => v) && 'Complete physical assessment • '}
                {!isBroken && !isUnexpectedlyBroken && !testResult.testingLevel && 'Select testing level'}
              </div>
            )}
          </>
        )}
      </div>
    )
  }

  // Success Screen
  if (step === 'success') {
    const drafts = submitResult?.listingServiceDrafts || []
    const brokenItemsCount = submitResult?.brokenItems || 0
    const brokenItemDetails = submitResult?.brokenItemDetails || []
    const repairItems = allItemResults.filter(r => r.needsRepair)
    const passedItems = allItemResults.filter(r => r.overallCondition !== 'Broken' && !r.needsRepair)
    const itemCount = deal?.items?.length || 0
    const isListingService = deal?.isListingService || false

    // Debug logging
    console.log('Success screen state:', {
      allItemResults,
      passedItems,
      brokenItemsCount,
      brokenItemDetails,
      isListingService,
      creatingDrafts,
      draftsCreated,
      submitResult,
      splitPerformed: submitResult?.splitPerformed,
      splitDealId: submitResult?.splitDealId,
      passedItemCount: submitResult?.passedItemCount,
      brokenItemCount: submitResult?.brokenItemCount
    })

    return (
      <div style={{ maxWidth: '560px', margin: '60px auto', padding: '0 24px' }}>
        <Card style={{ textAlign: 'center', padding: '40px' }}>
          <div style={{ fontSize: '64px', marginBottom: '16px' }}>✅</div>
          <h2 style={{ fontSize: '24px', fontWeight: 700, marginBottom: '12px' }}>Testing Complete</h2>
          <p style={{ color: theme.colors.grey500, marginBottom: '8px' }}>
            Test results saved for {itemCount} item{itemCount !== 1 ? 's' : ''}.
            {submitResult?.splitPerformed ? (
              ` Deal split: ${submitResult.passedItemCount} passed → Awaiting Payment, ${submitResult.brokenItemCount} broken → New Issue Trade`
            ) : submitResult?.targetStage && (
              ` Deal moved to ${
                submitResult.targetStage === 'contractsent' ? 'Tested/Awaiting Payment' :
                submitResult.targetStage === '588059587' ? 'Issue with Item' :
                submitResult.targetStage
              } stage.`
            )}
          </p>

          {/* Deal Info */}
          <div style={{
            background: theme.colors.grey100,
            borderRadius: theme.radius.md,
            padding: '12px 16px',
            marginBottom: '20px',
            fontSize: '14px'
          }}>
            <div style={{ fontWeight: 600, marginBottom: '4px' }}>Deal ID</div>
            <div style={{ fontFamily: theme.fonts.sans }}>{deal?.id}</div>
            <div style={{ color: theme.colors.grey500, marginTop: '4px' }}>{itemCount} item{itemCount !== 1 ? 's' : ''} tested</div>
          </div>

          {/* Listing Service - Shopify Drafts Auto-Created */}
          {deal?.isListingService && drafts.length > 0 && (
            <div style={{
              background: theme.colors.greenLight,
              border: `1px solid ${theme.colors.greenOnions}`,
              borderRadius: theme.radius.md,
              padding: '16px',
              marginBottom: '16px',
              textAlign: 'left'
            }}>
              <div style={{ fontWeight: 600, marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span>🛒</span> Shopify Drafts Created Automatically
              </div>
              {drafts.map((draft, i) => (
                <div key={i} style={{ fontSize: '13px', marginBottom: '4px' }}>
                  • {draft.itemName} <span style={{ color: theme.colors.grey500 }}>SKU: {draft.sku}</span>
                </div>
              ))}
            </div>
          )}

          {/* Listing Service - Issues Found */}
          {deal?.isListingService && submitResult?.listingServiceIssue && (
            <div style={{
              background: theme.colors.amberLight,
              border: `1px solid ${theme.colors.sunburst}`,
              borderRadius: theme.radius.md,
              padding: '16px',
              marginBottom: '16px',
              textAlign: 'left'
            }}>
              <div style={{ fontWeight: 600, marginBottom: '8px' }}>
                ⚠️ Listing Service Item Has Issues
              </div>
              <div style={{ fontSize: '13px', color: theme.colors.grey500 }}>
                Manual intervention required. Customer will be notified.
              </div>
            </div>
          )}

          {/* Broken Items */}
          {brokenItemDetails.length > 0 && (
            <div style={{
              background: theme.colors.redLight,
              border: `1px solid ${theme.colors.crimson}`,
              borderRadius: theme.radius.md,
              padding: '16px',
              marginBottom: '16px',
              textAlign: 'left'
            }}>
              <div style={{ fontWeight: 600, marginBottom: '8px' }}>
                ⚠️ {brokenItemDetails.length} Broken Item{brokenItemDetails.length !== 1 ? 's' : ''}
              </div>
              {brokenItemDetails.map((item, i) => (
                <div key={i} style={{ fontSize: '13px', marginBottom: '4px' }}>
                  • {item.itemName}: {item.issue}
                </div>
              ))}
            </div>
          )}

          {/* Items Marked for Repair */}
          {repairItems.length > 0 && (
            <div style={{
              background: theme.colors.amberLight,
              border: `1px solid ${theme.colors.sunburst}`,
              borderRadius: theme.radius.md,
              padding: '16px',
              marginBottom: '16px',
              textAlign: 'left'
            }}>
              <div style={{ fontWeight: 600, marginBottom: '8px' }}>
                🔧 {repairItems.length} Item{repairItems.length !== 1 ? 's' : ''} Marked for Repair
              </div>
              {repairItems.map((item, i) => (
                <div key={i} style={{ fontSize: '13px', marginBottom: '4px' }}>
                  • {item.itemName}: {item.repairIssues}
                </div>
              ))}
            </div>
          )}

          {/* Split Deal: Some items passed, some broken - split into two deals */}
          {submitResult?.splitPerformed && (
            <div style={{
              background: theme.colors.grey100,
              borderRadius: theme.radius.md,
              padding: '16px',
              marginBottom: '16px',
              fontSize: '14px',
              textAlign: 'left'
            }}>
              {/* Passed items - green */}
              <div style={{
                background: theme.colors.greenLight,
                border: `1px solid ${theme.colors.greenOnions}`,
                borderRadius: theme.radius.md,
                padding: '12px',
                marginBottom: '12px'
              }}>
                <div style={{ fontWeight: 600, marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '8px', color: theme.colors.greenOnions }}>
                  <span>✓</span> {submitResult.passedItemCount} Item{submitResult.passedItemCount !== 1 ? 's' : ''} Passed - Ready to List
                </div>
                {submitResult.passedItems?.map((item, i) => (
                  <div key={i} style={{ fontSize: '13px', marginLeft: '20px', color: theme.colors.grey600 }}>
                    • {item.name} - £{parseFloat(item.buyPrice || 0).toFixed(2)}
                  </div>
                ))}
              </div>

              {/* Broken items - amber/red */}
              <div style={{
                background: theme.colors.amberLight,
                border: `1px solid ${theme.colors.sunburst}`,
                borderRadius: theme.radius.md,
                padding: '12px'
              }}>
                <div style={{ fontWeight: 600, marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '8px', color: theme.colors.sunburst }}>
                  <span>⚠️</span> {submitResult.brokenItemCount} Item{submitResult.brokenItemCount !== 1 ? 's' : ''} Not Listed - Moved to New Issue Trade
                </div>
                <div style={{ fontSize: '12px', color: theme.colors.grey500, marginBottom: '8px', marginLeft: '28px' }}>
                  New Deal ID: <strong style={{ fontFamily: 'monospace', color: theme.colors.sunburst }}>{submitResult.splitDealId}</strong>
                </div>
                {submitResult.brokenItemsForSplit?.map((item, i) => (
                  <div key={i} style={{ fontSize: '13px', marginLeft: '20px', color: theme.colors.grey600 }}>
                    • {item.name} - Spares: £{parseFloat(item.sparesAmount || 0).toFixed(2)}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Regular Trade Items - Manual Draft Creation */}
          {/* Show if there are passed items AND either no broken items OR a split was performed */}
          {!isListingService && passedItems.length > 0 && (brokenItemDetails.length === 0 || submitResult?.splitPerformed) && !creatingDrafts && draftsCreated.length === 0 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', marginBottom: '16px' }}>
              <Button
                variant="success"
                onClick={() => setShowDraftConfirm(true)}
                fullWidth
                size="lg"
              >
                🛒 Create {passedItems.length} Shopify Draft{passedItems.length !== 1 ? 's' : ''} {submitResult?.splitPerformed ? '(Passed Items Only)' : ''}
              </Button>
              <Button
                variant="secondary"
                onClick={() => {
                  // TODO: Show individual item review screen
                  alert('Individual item review will be implemented')
                }}
                fullWidth
              >
                Review Items Individually
              </Button>
            </div>
          )}

          {/* Info: Deal going to Issue Trades because of broken items (non-split case) */}
          {!isListingService && brokenItemDetails.length > 0 && !submitResult?.splitPerformed && (
            <div style={{
              background: theme.colors.amberLight,
              border: `1px solid ${theme.colors.sunburst}`,
              borderRadius: theme.radius.md,
              padding: '16px',
              marginBottom: '16px',
              fontSize: '14px',
              textAlign: 'left'
            }}>
              <div style={{ fontWeight: 600, marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span>⚠️</span> Items Require Customer Response
              </div>
              <p style={{ margin: '0 0 8px 0', color: theme.colors.grey600 }}>
                This deal has {brokenItemDetails.length} broken item{brokenItemDetails.length !== 1 ? 's' : ''}. It will be moved to <strong>Issue with Item</strong> stage.
              </p>
              <p style={{ margin: 0, color: theme.colors.grey500, fontSize: '13px' }}>
                Shopify drafts will be created after the customer responds in Issue Trades.
              </p>
            </div>
          )}

          {/* Debug: Show why button might not appear */}
          {!isListingService && passedItems.length === 0 && brokenItemDetails.length === 0 && allItemResults.length > 0 && (
            <div style={{
              background: theme.colors.amberLight,
              border: `1px solid ${theme.colors.sunburst}`,
              borderRadius: theme.radius.md,
              padding: '12px',
              marginBottom: '16px',
              fontSize: '13px',
              textAlign: 'left'
            }}>
              <strong>No items eligible for draft creation:</strong>
              <div style={{ marginTop: '8px' }}>
                {allItemResults.map((r, i) => (
                  <div key={i}>• {r.itemName}: {r.overallCondition} {r.needsRepair ? '(needs repair)' : ''}</div>
                ))}
              </div>
            </div>
          )}

          {/* Draft Creation Progress */}
          {creatingDrafts && (
            <div style={{
              background: theme.colors.blueLight,
              border: `1px solid ${theme.colors.imBlue}`,
              borderRadius: theme.radius.md,
              padding: '20px',
              marginBottom: '16px',
              textAlign: 'left'
            }}>
              <div style={{ fontWeight: 600, marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                <Spinner size={16} /> Creating Shopify Drafts...
              </div>
              <div style={{
                background: theme.colors.grey200,
                borderRadius: '20px',
                height: '8px',
                overflow: 'hidden',
                marginBottom: '8px'
              }}>
                <div style={{
                  background: theme.colors.imBlue,
                  height: '100%',
                  width: `${(draftProgress.current / draftProgress.total) * 100}%`,
                  transition: 'width 0.3s ease'
                }} />
              </div>
              <div style={{ fontSize: '13px', color: theme.colors.grey500 }}>
                {draftProgress.current} of {draftProgress.total} drafts created
              </div>
            </div>
          )}

          {/* Draft Creation Complete */}
          {draftsCreated.length > 0 && !creatingDrafts && (
            <div style={{
              background: theme.colors.greenLight,
              border: `1px solid ${theme.colors.greenOnions}`,
              borderRadius: theme.radius.md,
              padding: '20px',
              marginBottom: '16px',
              textAlign: 'left'
            }}>
              <div style={{ fontWeight: 600, marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span style={{ fontSize: '20px' }}>✅</span> All Drafts Created!
              </div>
              <div style={{ fontSize: '14px', color: theme.colors.grey500, marginBottom: '12px' }}>
                {draftsCreated.filter(d => d.success).length} draft{draftsCreated.filter(d => d.success).length !== 1 ? 's' : ''} created
              </div>
              {draftsCreated.map((draft, i) => (
                <div key={i} style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '8px 12px',
                  background: theme.colors.white,
                  borderRadius: theme.radius.sm,
                  marginBottom: '6px'
                }}>
                  <span style={{ fontSize: '16px' }}>{draft.success ? '✅' : '❌'}</span>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 500, fontSize: '14px' }}>{draft.itemName}</div>
                    {draft.success && draft.draftId && (
                      <div style={{ fontSize: '12px', color: theme.colors.grey500 }}>ID: {draft.draftId}</div>
                    )}
                    {!draft.success && draft.error && (
                      <div style={{ fontSize: '12px', color: theme.colors.crimson }}>{draft.error}</div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}

          <Button variant="ghost" onClick={reset} fullWidth>
            {!isListingService && passedItems.length > 0 && draftsCreated.length === 0 ? 'Skip - Test Another Deal' : 'Done'}
          </Button>
        </Card>

        {/* Draft Confirmation Modal */}
        {showDraftConfirm && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            padding: '24px'
          }}>
            <Card style={{ maxWidth: '480px', width: '100%', padding: '24px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '16px' }}>
                <span style={{ fontSize: '24px' }}>🛒</span>
                <h3 style={{ fontSize: '18px', fontWeight: 700, margin: 0 }}>Create All Drafts</h3>
              </div>

              <p style={{ color: theme.colors.grey500, marginBottom: '16px', fontSize: '14px' }}>
                This will create {passedItems.length} draft listing{passedItems.length !== 1 ? 's' : ''} on Shopify.
              </p>

              <div style={{ marginBottom: '20px', maxHeight: '200px', overflowY: 'auto' }}>
                {passedItems.map((itemResult, i) => (
                  <div key={i} style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '10px 12px',
                    background: theme.colors.grey100,
                    borderRadius: theme.radius.sm,
                    marginBottom: '6px'
                  }}>
                    <span style={{ fontWeight: 500, fontSize: '14px' }}>
                      {itemResult.itemName}
                    </span>
                    <Badge color="green">{itemResult.overallCondition}</Badge>
                  </div>
                ))}
              </div>

              {draftError && <Alert type="error">{draftError}</Alert>}

              <div style={{ display: 'flex', gap: '12px' }}>
                <Button
                  variant="success"
                  onClick={() => createShopifyDrafts(passedItems)}
                  fullWidth
                  size="lg"
                >
                  Create All Drafts
                </Button>
                <Button
                  variant="secondary"
                  onClick={() => setShowDraftConfirm(false)}
                  fullWidth
                >
                  Cancel
                </Button>
              </div>
            </Card>
          </div>
        )}
      </div>
    )
  }

  return null
}

export default Testing
